// Taxa de Dificuldade de Entrega (FreteTda) — PO1-3066 / épico PO1-3044. No menu fica em
// Tabelas de Frete, porque compõe o preço do frete e não é catálogo geral.
// Regras de taxa de entrega cobradas por CPF/CNPJ do destinatário (TDA, TDE, TRT, Descarga,
// Agendamento, Outro). Cada regra é por embarcador + transportadora + CPF/CNPJ e aponta para
// uma taxa-âncora de catálogo que identifica o tipo; o calc-frete-service soma o valor antes
// do ICMS/ISS. useCompanies no padrão de VincularTipoServico porque a regra é por embarcador
// (taxa é catálogo global). Layout no padrão do Hub (PO1-2723/2724): PageHero, FilterBand,
// MetricCardGroup e ShellCard/TableSurface por token, sem cor cravada.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Select from "react-select";
import { FaFileUpload, FaPen, FaPlus } from "react-icons/fa";

import api from "services/api";
import { useToast } from "hooks/toast";
import EscolherArquivos from "components/EscolherArquivos";
import { useCompanies } from "hooks/companies";
import useDebounce from "hooks/useDebounce";
import SpecificEmpresaRequiredNotice from "components/SpecificEmpresaRequiredNotice";
import PageHero, { PageHeroRefreshButton } from "components/PageHero";
import FilterBand from "components/FilterBand";
import MetricCard from "components/MetricCard";
import MetricCardGroup from "components/MetricCardGroup";
import ShellCard, { TableSurface } from "components/ShellCard";
import { AUTOPILOT_COLORS } from "components/autopilot/styles";
import { reactSelectStyles, reactSelectPortalProps } from "utils/reactSelectStyles";
import {
  digitsOnly,
  formatDocument,
  isValidCnpj,
  isValidDocumento,
  mascaraDocumentoParcial,
} from "utils/formatDoc";
import {
  FRETE_TDA_TIPO_POR,
  rotuloPor,
  FRETE_TDA_COLUNAS_PLANILHA,
  FRETE_TDA_TIPOS,
  FRETE_TDA_TIPO_COR,
  tipoDoCodigo,
  rotuloAlcance,
  formatCepInput,
  getCepDigits,
  listarRegras,
  criarRegra,
  atualizarRegra,
  alterarStatusRegra,
  importarPlanilha,
  consultarRazaoSocial,
  mensagemErroFreteTda,
} from "services/freteTdaApi";

const STATUS_ALL = "__ALL__";
const TRANSP_ALL = "";

function emptyForm() {
  return {
    idTransportadora: "",
    cnpj: "",
    nome: "",
    descricao: "",
    // Tipo da regra, gravado no campo `codigo` (nao ha mais taxa-ancora).
    codigo: "TDE",
    tipo: "FIXO",
    por: "CTE",
    valor: "",
    valorMinimo: "",
    // Alcance (PO1-3085): pelo menos um criterio entre documento, UF e faixa de CEP;
    // cidade e faixa de CEP sao mutuamente exclusivas.
    idEstado: "",
    idCidade: "",
    cepInicial: "",
    cepFinal: "",
    status: true,
  };
}

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function isActive(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  return !["0", "false", "inativo", "nao", ""].includes(normalizeText(value));
}

function toNumberOrNull(value) {
  if (value === "" || value == null) return null;
  const num = Number(String(value).replace(",", "."));
  return Number.isNaN(num) ? null : num;
}

function formatValor(item) {
  const num = toNumberOrNull(item.valor);
  if (num == null) return "-";
  if (item.tipo === "PERCENTUAL") {
    return `${num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%`;
  }
  return `R$ ${num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatMinimo(item) {
  const num = toNumberOrNull(item.valorMinimo);
  if (num == null || num === 0) return "-";
  return `R$ ${num.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function tipoBadgeColor(codigo) {
  return FRETE_TDA_TIPO_COR[tipoDoCodigo(codigo)] || "default";
}

const modalHeaderStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "1rem 1.5rem 0.75rem",
  borderBottom: `1px solid ${AUTOPILOT_COLORS.border}`,
};

const iconButtonStyle = {
  width: 34,
  height: 34,
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
};

const helperStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem", marginTop: "0.25rem" };
const filterLabelStyle = { color: AUTOPILOT_COLORS.muted };

const PAGE_TABS = [
  { key: "regras", label: "Regras" },
  { key: "importar", label: "Importar planilha" },
];

const STATUS_OPTIONS = [
  { value: STATUS_ALL, label: "Todos" },
  { value: "active", label: "Ativas" },
  { value: "inactive", label: "Inativas" },
];

const tabRowStyle = { display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.9rem" };

function tabPillStyle(active) {
  return {
    border: `1px solid ${active ? AUTOPILOT_COLORS.accent : AUTOPILOT_COLORS.border}`,
    background: active ? "var(--theme-accent-glow)" : "transparent",
    color: active ? AUTOPILOT_COLORS.accent : AUTOPILOT_COLORS.muted,
    borderRadius: 999,
    padding: "0.3rem 0.95rem",
    fontSize: "0.8rem",
    fontWeight: active ? 700 : 500,
    cursor: "pointer",
    transition: "border-color 0.2s ease, color 0.2s ease",
  };
}

const stateBoxStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.6rem",
  padding: "2rem 1rem",
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.86rem",
};

const numericCellStyle = {
  verticalAlign: "middle",
  whiteSpace: "nowrap",
  fontVariantNumeric: "tabular-nums",
};

const strongCellStyle = { color: AUTOPILOT_COLORS.title, fontWeight: 600 };
const subCellStyle = { color: AUTOPILOT_COLORS.muted, fontSize: "0.78rem" };

const insetPanelStyle = {
  border: `1px solid ${AUTOPILOT_COLORS.border}`,
  background: AUTOPILOT_COLORS.insetSurface,
  borderRadius: 12,
  padding: "0.85rem 1rem",
  marginBottom: "1rem",
};

const insetEyebrowStyle = {
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.68rem",
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  marginBottom: "0.5rem",
};

const colunaNomeStyle = {
  width: "22%",
  fontWeight: 600,
  fontFamily: "monospace",
  color: AUTOPILOT_COLORS.title,
};

function toastError(addToast, title, error, fallback) {
  addToast({
    place: "tr",
    type: "danger",
    title,
    description: mensagemErroFreteTda(error, fallback),
    icon: "tim-icons icon-alert-circle-exc",
  });
}

// Mensagem do aviso de regra sem restrição de alcance (PO1-3085). Não é erro de preenchimento:
// é uma confirmação, porque a regra passa a valer para toda entrega daquela transportadora.
const SEM_RESTRICAO_AVISO =
  "Sem CPF/CNPJ, UF ou faixa de CEP, esta regra vale para TODAS as entregas desta transportadora. " +
  "Se for isso mesmo, confirme abaixo.";

const FreteTda = () => {
  const { addToast } = useToast();
  const {
    empresaId,
    hasEmpresaSelection,
    transportadoraId,
    hasTransportadoraSelection,
  } = useCompanies();

  const [pageTab, setPageTab] = useState("regras");

  // Listagem
  const [regras, setRegras] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_ALL);
  const [transportadoraFilter, setTransportadoraFilter] = useState(TRANSP_ALL);
  const debouncedSearch = useDebounce(search, 300);

  // Catálogos
  const [transportadoras, setTransportadoras] = useState([]);
  // Alcance (PO1-3085): UF e cidade vem dos endpoints que ja existem.
  const [estados, setEstados] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [carregandoCidades, setCarregandoCidades] = useState(false);

  // Modal criar/editar
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  // PO1-3085 (12/09/2026): regra SEM nenhum critério de alcance passou a ser válida — a
  // Transoliveira cobra a mesma TDE de qualquer destinatário, em qualquer praça. Como ela vale
  // para TODA entrega daquela transportadora, não pode sair por descuido: o usuário confirma
  // uma vez, e só então o payload leva `confirmaSemRestricao` (que o backend exige).
  const [confirmouSemRestricao, setConfirmouSemRestricao] = useState(false);
  const [razaoSocialPreview, setRazaoSocialPreview] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  // Importação por planilha
  const [importTransportadora, setImportTransportadora] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [previewResult, setPreviewResult] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingImport, setLoadingImport] = useState(false);

  // Transportadora efetiva do filtro: a do header trava o select.
  const effectiveTransportadoraFilter = hasTransportadoraSelection
    ? String(transportadoraId)
    : transportadoraFilter;

  // O seletor do cabeçalho manda: ao escolher uma transportadora ela vale para o filtro e para
  // a importação; ao voltar para "todas", os dois voltam a ficar vazios. Sem isso o estado local
  // guardava a transportadora anterior com o campo já habilitado, e a planilha ia para a
  // transportadora errada sem nenhum aviso.
  useEffect(() => {
    const doCabecalho = hasTransportadoraSelection ? String(transportadoraId) : TRANSP_ALL;
    setTransportadoraFilter(doCabecalho);
    setImportTransportadora(doCabecalho);
    // A pré-visualização vale para uma transportadora só; trocar o cabeçalho invalida.
    setPreviewResult(null);
    setImportResult(null);
  }, [hasTransportadoraSelection, transportadoraId]);

  const loadRegras = useCallback(async () => {
    if (!hasEmpresaSelection) {
      setRegras([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const params = { idEmbarcador: empresaId };
      if (effectiveTransportadoraFilter) params.idTransportadora = effectiveTransportadoraFilter;
      if (statusFilter === "active") params.status = true;
      if (statusFilter === "inactive") params.status = false;
      const termo = debouncedSearch.trim();
      if (termo) {
        const digits = digitsOnly(termo);
        if (digits.length >= 4 && digits.length === termo.replace(/[.\-/\s]/g, "").length) {
          params.cnpj = digits;
        } else {
          params.search = termo;
        }
      }
      const items = await listarRegras(params);
      setRegras(items);
    } catch (error) {
      setRegras([]);
      toastError(addToast, "Erro ao carregar", error, "Não foi possível buscar as regras de TDA/TDE.");
    } finally {
      setLoading(false);
    }
  }, [addToast, debouncedSearch, effectiveTransportadoraFilter, empresaId, hasEmpresaSelection, statusFilter]);

  useEffect(() => {
    loadRegras();
  }, [loadRegras]);

  // Transportadoras do embarcador (select do filtro, do modal e da importação).
  useEffect(() => {
    if (!hasEmpresaSelection) {
      setTransportadoras([]);
      return undefined;
    }
    let mounted = true;
    (async () => {
      try {
        const res = await api.get(`/transportadoras/${empresaId}`);
        if (!mounted) return;
        const options = (Array.isArray(res.data) ? res.data : []).map((item) => ({
          value: String(item.transportadora_id),
          label: item.transportadora_fantasia || item.transportadora_razao || `#${item.transportadora_id}`,
        }));
        setTransportadoras(options);
      } catch (error) {
        if (mounted) setTransportadoras([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [empresaId, hasEmpresaSelection]);

  // UFs para o alcance por praça. Molde de Recalcular/index.js: catálogo global, sem
  // filtro por embarcador.
  useEffect(() => {
    let mounted = true;
    api
      .get("/frete/estados")
      .then((response) => {
        if (mounted) setEstados(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        if (mounted) setEstados([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Cidades da UF escolhida. Reseta a cidade quando a UF muda e a escolhida não pertence
  // mais à lista — o mesmo cuidado do Recalcular, que blinda contra preset e edição.
  useEffect(() => {
    let mounted = true;
    if (!form.idEstado) {
      setCidades([]);
      return () => {
        mounted = false;
      };
    }
    setCarregandoCidades(true);
    api
      .get(`/frete/estados/${form.idEstado}/cidades`)
      .then((response) => {
        if (!mounted) return;
        const lista = Array.isArray(response.data) ? response.data : [];
        setCidades(lista);
        setForm((current) => {
          if (!current.idCidade) return current;
          const existe = lista.some((c) => String(c.id) === String(current.idCidade));
          return existe ? current : { ...current, idCidade: "" };
        });
      })
      .catch(() => {
        if (mounted) setCidades([]);
      })
      .finally(() => {
        if (mounted) setCarregandoCidades(false);
      });
    return () => {
      mounted = false;
    };
  }, [form.idEstado]);

  const transportadoraLabel = useCallback(
    (id) => {
      const found = transportadoras.find((t) => String(t.value) === String(id));
      return found ? found.label : null;
    },
    [transportadoras]
  );

  // Filtro local complementar (o backend já filtra; isto só garante a resposta no cliente).
  const filtered = useMemo(() => {
    const query = normalizeText(debouncedSearch);
    const queryDigits = digitsOnly(debouncedSearch);
    return regras.filter((item) => {
      if (statusFilter === "active" && !isActive(item.status)) return false;
      if (statusFilter === "inactive" && isActive(item.status)) return false;
      if (!query) return true;
      if (queryDigits.length >= 4 && digitsOnly(item.cnpj).includes(queryDigits)) return true;
      return normalizeText(
        [item.nome, item.descricao, item.nomeTransportadora, item.codigo, rotuloAlcance(item)].join(" ")
      ).includes(query);
    });
  }, [debouncedSearch, regras, statusFilter]);

  // Indicadores do recorte (MetricCardGroup) e estado da faixa de filtros.
  const resumo = useMemo(() => {
    const ativas = filtered.filter((item) => isActive(item.status)).length;
    const transportadorasDistintas = new Set(
      filtered.map((item) => String(item.idTransportadora))
    );
    return {
      total: filtered.length,
      ativas,
      inativas: filtered.length - ativas,
      transportadoras: transportadorasDistintas.size,
    };
  }, [filtered]);

  const transportadoraOptions = useMemo(
    () => [{ value: TRANSP_ALL, label: "Todas" }, ...transportadoras],
    [transportadoras]
  );

  const transportadoraOption =
    transportadoraOptions.find(
      (opt) => String(opt.value) === String(effectiveTransportadoraFilter)
    ) || transportadoraOptions[0];

  const statusOption =
    STATUS_OPTIONS.find((opt) => opt.value === statusFilter) || STATUS_OPTIONS[0];

  const hasFilters =
    normalizeText(search) !== "" ||
    statusFilter !== STATUS_ALL ||
    (!hasTransportadoraSelection && transportadoraFilter !== TRANSP_ALL);

  const activeFiltersCount =
    (statusFilter !== STATUS_ALL ? 1 : 0) +
    (!hasTransportadoraSelection && transportadoraFilter !== TRANSP_ALL ? 1 : 0);

  const clearFilters = () => {
    setSearch("");
    setStatusFilter(STATUS_ALL);
    if (!hasTransportadoraSelection) setTransportadoraFilter(TRANSP_ALL);
  };

  // ── Modal ────────────────────────────────────────────────────────────────

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...emptyForm(),
      idTransportadora: effectiveTransportadoraFilter || "",
    });
    setErrors({});
    setConfirmouSemRestricao(false);
    setRazaoSocialPreview(null);
    setModalOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setConfirmouSemRestricao(false);
    setForm({
      idTransportadora: item.idTransportadora != null ? String(item.idTransportadora) : "",
      cnpj: digitsOnly(item.cnpj),
      nome: item.nome || "",
      descricao: item.descricao || "",
      codigo: tipoDoCodigo(item.codigo),
      tipo: item.tipo || "FIXO",
      por: item.por || "",
      valor: item.valor != null ? String(item.valor) : "",
      valorMinimo: item.valorMinimo != null && Number(item.valorMinimo) !== 0 ? String(item.valorMinimo) : "",
      idEstado: item.idEstado != null ? String(item.idEstado) : "",
      idCidade: item.idCidade != null ? String(item.idCidade) : "",
      cepInicial: item.cepInicial != null ? formatCepInput(String(item.cepInicial).padStart(8, "0")) : "",
      cepFinal: item.cepFinal != null ? formatCepInput(String(item.cepFinal).padStart(8, "0")) : "",
      status: isActive(item.status),
    });
    setErrors({});
    setRazaoSocialPreview(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const updateForm = (field, value) => {
    // Mexeu no alcance: a confirmação de "vale para todas as entregas" perde a validade. Fica
    // FORA dos updaters de setForm/setErrors de propósito — eles têm que ser puros, e o React
    // pode chamá-los duas vezes. Sem isto, a confirmação ficaria pendurada depois de o usuário
    // preencher uma UF, com o aviso na tela contradizendo o formulário.
    if (["cnpj", "idEstado", "idCidade", "cepInicial", "cepFinal"].includes(field)) {
      setConfirmouSemRestricao(false);
    }
    setForm((current) => {
      if (field === "tipo") {
        const allowed = FRETE_TDA_TIPO_POR[value] || [];
        return { ...current, tipo: value, por: allowed.includes(current.por) ? current.por : allowed[0] || "" };
      }
      if (field === "cnpj") {
        return { ...current, cnpj: digitsOnly(value).slice(0, 14) };
      }
      // Alcance (PO1-3085). Trocar a UF zera a cidade (o efeito de carga confirma depois).
      if (field === "idEstado") {
        return { ...current, idEstado: value, idCidade: "" };
      }
      // Cidade e faixa de CEP sao MUTUAMENTE EXCLUSIVAS: escolher uma limpa a outra, em vez
      // de deixar o usuario descobrir pelo erro de validacao.
      if (field === "idCidade") {
        return value
          ? { ...current, idCidade: value, cepInicial: "", cepFinal: "" }
          : { ...current, idCidade: value };
      }
      if (field === "cepInicial" || field === "cepFinal") {
        const mascarado = formatCepInput(value);
        const base = mascarado ? { ...current, idCidade: "" } : current;
        return { ...base, [field]: mascarado };
      }
      return { ...current, [field]: value };
    });
    setErrors((current) => {
      // Além do erro do próprio campo, limpa os erros CROSS-FIELD do grupo de alcance:
      // eles moram numa chave diferente da que o usuário acabou de mexer (o "pelo menos um
      // critério" fica em `alcance`, e "a cidade exige a UF" em `idEstado`), então sem isso
      // a mensagem ficava presa na tela mesmo depois de corrigida — achado do QA de 12/09.
      const CAMPOS_DE_ALCANCE = ["cnpj", "idEstado", "idCidade", "cepInicial", "cepFinal"];
      const aLimpar = [field];
      if (CAMPOS_DE_ALCANCE.includes(field)) aLimpar.push(...CAMPOS_DE_ALCANCE, "alcance");
      if (!aLimpar.some((chave) => current[chave])) return current;
      const next = { ...current };
      aLimpar.forEach((chave) => delete next[chave]);
      return next;
    });
  };

  // Lookup opcional da razão social só para CNPJ válido (CPF não tem lookup); preenche o nome se vazio.
  useEffect(() => {
    if (!modalOpen) return undefined;
    let active = true;
    if (form.cnpj.length !== 14 || !isValidCnpj(form.cnpj)) {
      setRazaoSocialPreview(null);
      setLookupLoading(false);
      return undefined;
    }
    setLookupLoading(true);
    consultarRazaoSocial(form.cnpj).then((nome) => {
      if (!active) return;
      setRazaoSocialPreview(nome);
      setLookupLoading(false);
      if (nome) {
        setForm((current) => (current.nome.trim() ? current : { ...current, nome: nome.slice(0, 60) }));
      }
    });
    return () => {
      active = false;
    };
  }, [form.cnpj, modalOpen]);

  const validate = () => {
    const next = {};
    if (!form.idTransportadora) next.idTransportadora = "Selecione a transportadora.";

    // PO1-3085: o documento passou a ser OPCIONAL (a regra pode valer por praça), mas quando
    // informado segue validado por dígito verificador.
    if (form.cnpj) {
      if (form.cnpj.length !== 11 && form.cnpj.length !== 14) {
        next.cnpj = "Informe um CPF (11 dígitos) ou CNPJ (14 dígitos).";
      } else if (!isValidDocumento(form.cnpj)) {
        next.cnpj = `${form.cnpj.length === 11 ? "CPF" : "CNPJ"} inválido (dígito verificador não confere).`;
      }
    }

    // ---- Alcance: as mesmas 4 regras das restrições do banco -------------------
    const cepIni = getCepDigits(form.cepInicial);
    const cepFim = getCepDigits(form.cepFinal);
    const temCep = Boolean(cepIni || cepFim);
    if (temCep) {
      if (!cepIni || cepIni.length !== 8) next.cepInicial = "Informe o CEP inicial com 8 dígitos.";
      if (!cepFim || cepFim.length !== 8) next.cepFinal = "Informe o CEP final com 8 dígitos.";
      if (cepIni.length === 8 && cepFim.length === 8 && Number(cepFim) < Number(cepIni)) {
        next.cepFinal = "O CEP final não pode ser menor que o inicial.";
      }
    }
    if (form.idCidade && temCep) {
      next.idCidade = "Informe cidade OU faixa de CEP, não os dois.";
    }
    if (form.idCidade && !form.idEstado) {
      next.idEstado = "A cidade exige a UF.";
    }
    if (!form.cnpj && !form.idEstado && !temCep && !confirmouSemRestricao) {
      next.alcance = SEM_RESTRICAO_AVISO;
    }

    if (!form.nome.trim()) next.nome = "Informe o nome da regra.";
    if (!form.codigo) next.codigo = "Selecione o tipo.";
    if (!form.tipo) next.tipo = "Selecione o cálculo.";
    if (!form.por) next.por = "Selecione a incidência.";
    const valor = toNumberOrNull(form.valor);
    if (valor == null) next.valor = "Informe um valor numérico.";
    // O backend exige > 0 ("Valor deve ser maior que zero"); sem o `<= 0` aqui, um zero só
    // era recusado no round-trip, via toast, em vez de no próprio campo.
    else if (valor <= 0) next.valor = "O valor deve ser maior que zero.";
    else if (form.tipo === "PERCENTUAL" && valor > 100) next.valor = "Percentual acima de 100%.";
    if (form.valorMinimo !== "") {
      const minimo = toNumberOrNull(form.valorMinimo);
      if (minimo == null) next.valorMinimo = "Informe um valor numérico.";
      else if (minimo < 0) next.valorMinimo = "O mínimo não pode ser negativo.";
    }
    return next;
  };

  const save = async () => {
    const nextErrors = validate();
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    const payload = {
      idEmbarcador: empresaId,
      idTransportadora: Number(form.idTransportadora),
      cnpj: form.cnpj,
      nome: form.nome.trim(),
      descricao: form.descricao.trim(),
      tipo: form.tipo,
      por: form.por,
      valor: toNumberOrNull(form.valor),
      valorMinimo: form.valorMinimo === "" ? null : toNumberOrNull(form.valorMinimo),
      codigo: form.codigo,
      // Alcance: vazio vira null, que no banco significa "nao restringe".
      idEstado: form.idEstado ? Number(form.idEstado) : null,
      idCidade: form.idCidade ? Number(form.idCidade) : null,
      cepInicial: getCepDigits(form.cepInicial) || null,
      cepFinal: getCepDigits(form.cepFinal) || null,
      status: Boolean(form.status),
      // Só vai quando o usuário confirmou de fato; o backend recusa alcance vazio sem isso.
      confirmaSemRestricao: confirmouSemRestricao,
    };
    try {
      setSaving(true);
      const resposta = editing
        ? await atualizarRegra(editing.id, payload)
        : await criarRegra(payload);

      addToast({
        place: "tr",
        type: "success",
        title: editing ? "Regra atualizada" : "Regra cadastrada",
        description: editing
          ? "As alterações foram salvas. Recalcule os CT-es afetados se necessário."
          : "A regra foi criada e já vale para o próximo cálculo.",
        icon: "tim-icons icon-check-2",
      });

      // Aviso de sobreposição (PO1-3085). NÃO bloqueia: a regra já foi salva. Com a
      // precedência, cadastro sobreposto não produz valor errado — a mais específica vence —
      // então o aviso serve para o usuário entender por que a regra não vai pegar em um caso.
      const aviso = resposta && resposta.aviso;
      if (aviso && aviso.mensagem) {
        const detalhe = Array.isArray(aviso.regras)
          ? aviso.regras
              .map((r) => `${r.nome || "regra"}${r.alcance ? ` (${r.alcance})` : ""}`)
              .join("; ")
          : "";
        addToast({
          place: "tr",
          type: "warning",
          title: "Regra mais específica já existe",
          description: detalhe ? `${aviso.mensagem} ${detalhe}.` : aviso.mensagem,
          icon: "tim-icons icon-alert-circle-exc",
        });
      }

      setModalOpen(false);
      await loadRegras();
    } catch (error) {
      toastError(addToast, "Erro ao salvar", error, "Não foi possível salvar a regra.");
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (item) => {
    try {
      await alterarStatusRegra(item.id, !isActive(item.status));
      await loadRegras();
    } catch (error) {
      toastError(addToast, "Erro ao alterar status", error, "Não foi possível alterar o status da regra.");
    }
  };

  // ── Importação ───────────────────────────────────────────────────────────

  const isBusy = loadingPreview || loadingImport;
  const canPreview = Boolean(selectedFile) && Boolean(importTransportadora) && hasEmpresaSelection;
  // Trocar arquivo ou transportadora zera a pré-visualização, então basta ela existir e ter
  // pelo menos uma linha válida.
  const canImport = canPreview && previewResult && Number(previewResult.linhasValidas) > 0;

  const handleFileChange = (event) => {
    const file = event.target.files ? event.target.files[0] : null;
    setSelectedFile(file);
    setPreviewResult(null);
    setImportResult(null);
  };

  const handlePreview = async () => {
    if (!canPreview || isBusy) return;
    try {
      setLoadingPreview(true);
      setImportResult(null);
      const result = await importarPlanilha({
        file: selectedFile,
        idEmbarcador: empresaId,
        idTransportadora: importTransportadora,
        dryRun: true,
      });
      setPreviewResult(result);
    } catch (error) {
      setPreviewResult(null);
      toastError(addToast, "Erro na pré-visualização", error, "Não foi possível ler a planilha.");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleImport = async () => {
    if (!canImport || isBusy) return;
    try {
      setLoadingImport(true);
      const result = await importarPlanilha({
        file: selectedFile,
        idEmbarcador: empresaId,
        idTransportadora: importTransportadora,
        dryRun: false,
      });
      setImportResult(result);
      addToast({
        place: "tr",
        type: "success",
        title: "Importação concluída",
        description: `${Number(result.inseridos || 0)} regra(s) inserida(s), ${Number(result.ignorados || 0)} ignorada(s).`,
        icon: "tim-icons icon-check-2",
      });
      await loadRegras();
    } catch (error) {
      toastError(addToast, "Erro na importação", error, "Não foi possível importar a planilha.");
    } finally {
      setLoadingImport(false);
    }
  };

  const handleResetImport = () => {
    setSelectedFile(null);
    setPreviewResult(null);
    setImportResult(null);
    setFileInputKey((k) => k + 1);
  };

  const renderWarnings = (warnings) => {
    const list = Array.isArray(warnings) ? warnings : [];
    if (!list.length) return <p style={{ color: "#9aa6c7", marginBottom: 0 }}>Nenhum aviso.</p>;
    return (
      <ul style={{ paddingLeft: "1.2rem", marginBottom: 0, maxHeight: 220, overflowY: "auto" }}>
        {list.map((w, index) => {
          if (typeof w === "string") return <li key={`w-${index}`}>{w}</li>;
          const linha = w && w.linha != null ? `Linha ${w.linha}: ` : "";
          const motivo = (w && (w.motivo || w.message || w.error)) || JSON.stringify(w);
          return <li key={`w-${index}`}>{linha}{motivo}</li>;
        })}
      </ul>
    );
  };

  const renderResultado = (titulo, result, isPreview) => (
    <Card style={{ marginTop: "1rem" }}>
      <CardBody>
        <h4 style={{ marginBottom: "0.75rem" }}>{titulo}</h4>
        <Row>
          <Col md="3"><strong>Linhas lidas:</strong> {Number(result.linhasLidas || 0)}</Col>
          <Col md="3"><strong>Linhas válidas:</strong> {Number(result.linhasValidas || 0)}</Col>
          {!isPreview ? (
            <>
              <Col md="3"><strong>Inseridas:</strong> {Number(result.inseridos || 0)}</Col>
              <Col md="3"><strong>Ignoradas:</strong> {Number(result.ignorados || 0)}</Col>
            </>
          ) : null}
        </Row>
        {isPreview && !(Number(result.linhasValidas) > 0) ? (
          <div className="alert alert-warning" style={{ marginTop: "0.75rem" }}>
            Nenhuma linha válida para importação. Corrija os avisos abaixo e pré-visualize de novo.
          </div>
        ) : null}
        <h5 style={{ marginTop: "1rem" }}>Avisos</h5>
        {renderWarnings(result.warnings)}
      </CardBody>
    </Card>
  );

  // ── Render ───────────────────────────────────────────────────────────────

  if (!hasEmpresaSelection) {
    return (
      <div className="content">
        <SpecificEmpresaRequiredNotice
          title="Taxa de Dificuldade de Entrega"
          message="Selecione um embarcador específico no topo da página para cadastrar as regras de Taxa de Dificuldade de Entrega."
        />
      </div>
    );
  }

  const porOptions = FRETE_TDA_TIPO_POR[form.tipo] || [];

  return (
    <div className="content">
      <Modal isOpen={modalOpen} toggle={closeModal} modalClassName="modal-align-top" fade={false} size="lg">
        <div style={modalHeaderStyle}>
          <div>
            <div style={{ fontWeight: 600, fontSize: "0.95rem", color: "#dbe8ff" }}>
              {editing ? `Regra ${form.nome || formatDocument(form.cnpj)}` : "Nova regra de TDA/TDE"}
            </div>
            <div style={{ fontSize: "0.8rem", color: "#8ca8d8", marginTop: 2 }}>
              Taxa de entrega cobrada pela transportadora para um CPF/CNPJ de destinatário; somada antes do ICMS/ISS.
            </div>
          </div>
          <Button close onClick={closeModal} aria-label="Fechar" />
        </div>

        <ModalBody style={{ padding: "1.25rem 1.5rem" }}>
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="tda-transportadora">Transportadora</Label>
                <Input
                  id="tda-transportadora"
                  type="select"
                  value={form.idTransportadora}
                  invalid={Boolean(errors.idTransportadora)}
                  disabled={Boolean(editing)}
                  onChange={(e) => updateForm("idTransportadora", e.target.value)}
                >
                  <option value="">Selecione</option>
                  {transportadoras.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Input>
                {errors.idTransportadora ? <FormFeedback>{errors.idTransportadora}</FormFeedback> : null}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="tda-cnpj">CPF/CNPJ do destinatário <span style={helperStyle}>(opcional)</span></Label>
                <Input
                  id="tda-cnpj"
                  value={mascaraDocumentoParcial(form.cnpj)}
                  placeholder="CPF ou CNPJ"
                  inputMode="numeric"
                  maxLength={18}
                  invalid={Boolean(errors.cnpj)}
                  onChange={(e) => updateForm("cnpj", e.target.value)}
                />
                {errors.cnpj ? <FormFeedback>{errors.cnpj}</FormFeedback> : null}
                {!errors.cnpj && lookupLoading ? <div style={helperStyle}>Consultando razão social...</div> : null}
                {!errors.cnpj && !lookupLoading && razaoSocialPreview ? (
                  <div style={{ ...helperStyle, color: "#9ddcff" }}>{razaoSocialPreview}</div>
                ) : null}
                {!errors.cnpj && !form.cnpj ? (
                  <div style={helperStyle}>Sem documento, a regra vale para qualquer destinatário da praça.</div>
                ) : null}
              </FormGroup>
            </Col>
          </Row>

          {/* Alcance por praça (PO1-3085). Cidade e faixa de CEP são mutuamente exclusivas: o
              updateForm limpa uma quando a outra é preenchida. */}
          <div style={insetPanelStyle}>
            <div style={insetEyebrowStyle}>Alcance por praça</div>
            <Row>
              <Col md="3">
                <FormGroup>
                  <Label for="tda-uf">UF</Label>
                  <Input
                    id="tda-uf"
                    type="select"
                    value={form.idEstado}
                    invalid={Boolean(errors.idEstado)}
                    onChange={(e) => updateForm("idEstado", e.target.value)}
                  >
                    <option value="">Todas</option>
                    {estados.map((estado) => (
                      <option key={estado.id} value={String(estado.id)}>{estado.uf}</option>
                    ))}
                  </Input>
                  {errors.idEstado ? <FormFeedback>{errors.idEstado}</FormFeedback> : null}
                </FormGroup>
              </Col>
              <Col md="5">
                <FormGroup>
                  <Label for="tda-cidade">Cidade</Label>
                  <Input
                    id="tda-cidade"
                    type="select"
                    value={form.idCidade}
                    invalid={Boolean(errors.idCidade)}
                    disabled={!form.idEstado || carregandoCidades}
                    onChange={(e) => updateForm("idCidade", e.target.value)}
                  >
                    <option value="">{form.idEstado ? "Todas do estado" : "Escolha a UF"}</option>
                    {cidades.map((cidade) => (
                      <option key={cidade.id} value={String(cidade.id)}>{cidade.nome}</option>
                    ))}
                  </Input>
                  {errors.idCidade ? <FormFeedback>{errors.idCidade}</FormFeedback> : null}
                  {!errors.idCidade && carregandoCidades ? (
                    <div style={helperStyle}>Carregando cidades...</div>
                  ) : null}
                </FormGroup>
              </Col>
              <Col md="2">
                <FormGroup>
                  <Label for="tda-cep-inicial">CEP inicial</Label>
                  <Input
                    id="tda-cep-inicial"
                    value={form.cepInicial}
                    placeholder="00000-000"
                    inputMode="numeric"
                    maxLength={9}
                    invalid={Boolean(errors.cepInicial)}
                    onChange={(e) => updateForm("cepInicial", e.target.value)}
                  />
                  {errors.cepInicial ? <FormFeedback>{errors.cepInicial}</FormFeedback> : null}
                </FormGroup>
              </Col>
              <Col md="2">
                <FormGroup>
                  <Label for="tda-cep-final">CEP final</Label>
                  <Input
                    id="tda-cep-final"
                    value={form.cepFinal}
                    placeholder="00000-000"
                    inputMode="numeric"
                    maxLength={9}
                    invalid={Boolean(errors.cepFinal)}
                    onChange={(e) => updateForm("cepFinal", e.target.value)}
                  />
                  {errors.cepFinal ? <FormFeedback>{errors.cepFinal}</FormFeedback> : null}
                </FormGroup>
              </Col>
            </Row>
            <div style={helperStyle}>
              Informe <strong>pelo menos um</strong> critério: CPF/CNPJ, UF (com ou sem cidade) ou
              faixa de CEP. Cidade e faixa de CEP não podem ser usadas juntas. Quando existir uma
              regra mais específica do mesmo tipo, é ela que vale.
            </div>
            {errors.alcance ? (
              <div style={{ ...helperStyle, color: "#ff8d72" }}>
                {errors.alcance}
                {errors.alcance === SEM_RESTRICAO_AVISO ? (
                  <div style={{ marginTop: 8 }}>
                    <Button
                      size="sm"
                      color="warning"
                      type="button"
                      onClick={() => {
                        // Confirma e já limpa o aviso: o próximo Salvar passa direto e leva
                        // `confirmaSemRestricao` no payload.
                        setConfirmouSemRestricao(true);
                        setErrors((current) => {
                          const next = { ...current };
                          delete next.alcance;
                          return next;
                        });
                      }}
                    >
                      Vale para todas as entregas, cadastrar assim
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
            {confirmouSemRestricao ? (
              <div style={{ ...helperStyle, color: "#ffd600" }}>
                Confirmado: esta regra vale para <strong>todas as entregas</strong> desta
                transportadora, sem restrição de destinatário nem de praça.
              </div>
            ) : null}
          </div>
          <Row>
            <Col md="8">
              <FormGroup>
                <Label for="tda-nome">Nome da regra</Label>
                <Input
                  id="tda-nome"
                  value={form.nome}
                  maxLength={60}
                  placeholder="Ex.: TDE Soma Autopeças"
                  invalid={Boolean(errors.nome)}
                  onChange={(e) => updateForm("nome", e.target.value)}
                />
                {errors.nome ? <FormFeedback>{errors.nome}</FormFeedback> : null}
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="tda-status">Status</Label>
                <Input
                  id="tda-status"
                  type="select"
                  value={form.status ? "active" : "inactive"}
                  onChange={(e) => updateForm("status", e.target.value === "active")}
                >
                  <option value="active">Ativa</option>
                  <option value="inactive">Inativa</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label for="tda-descricao">Descrição</Label>
            <Input
              id="tda-descricao"
              type="textarea"
              rows="2"
              maxLength={255}
              value={form.descricao}
              onChange={(e) => updateForm("descricao", e.target.value)}
            />
          </FormGroup>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label for="tda-tipo-regra">Tipo</Label>
                <Input
                  id="tda-tipo-regra"
                  type="select"
                  value={form.codigo}
                  invalid={Boolean(errors.codigo)}
                  onChange={(e) => updateForm("codigo", e.target.value)}
                >
                  <option value="">Selecione</option>
                  {FRETE_TDA_TIPOS.map((t) => (
                    <option key={t.codigo} value={t.codigo}>{t.nome}</option>
                  ))}
                </Input>
                {errors.codigo ? <FormFeedback>{errors.codigo}</FormFeedback> : null}
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="tda-calculo">Cálculo</Label>
                <Input
                  id="tda-calculo"
                  type="select"
                  value={form.tipo}
                  invalid={Boolean(errors.tipo)}
                  onChange={(e) => updateForm("tipo", e.target.value)}
                >
                  {Object.keys(FRETE_TDA_TIPO_POR).map((tipo) => (
                    <option key={tipo} value={tipo}>{tipo === "PERCENTUAL" ? "Percentual" : "Valor fixo"}</option>
                  ))}
                </Input>
                {errors.tipo ? <FormFeedback>{errors.tipo}</FormFeedback> : null}
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="tda-por">Incide sobre</Label>
                <Input
                  id="tda-por"
                  type="select"
                  value={form.por}
                  invalid={Boolean(errors.por)}
                  onChange={(e) => updateForm("por", e.target.value)}
                >
                  <option value="">Selecione</option>
                  {porOptions.map((por) => (
                    <option key={por} value={por}>{por} ({rotuloPor(form.tipo, por)})</option>
                  ))}
                </Input>
                {errors.por ? <FormFeedback>{errors.por}</FormFeedback> : null}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label for="tda-valor">{form.tipo === "PERCENTUAL" ? "Valor (%)" : "Valor (R$)"}</Label>
                <Input
                  id="tda-valor"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.valor}
                  invalid={Boolean(errors.valor)}
                  onChange={(e) => updateForm("valor", e.target.value)}
                />
                {errors.valor ? <FormFeedback>{errors.valor}</FormFeedback> : null}
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label for="tda-valor-minimo">Valor mínimo (R$)</Label>
                <Input
                  id="tda-valor-minimo"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.valorMinimo}
                  placeholder="opcional"
                  invalid={Boolean(errors.valorMinimo)}
                  onChange={(e) => updateForm("valorMinimo", e.target.value)}
                />
                {errors.valorMinimo ? <FormFeedback>{errors.valorMinimo}</FormFeedback> : null}
              </FormGroup>
            </Col>
          </Row>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
            <Button color="default" outline onClick={closeModal} disabled={saving}>Cancelar</Button>
            <Button color="info" onClick={save} disabled={saving}>{saving ? "Salvando..." : "Salvar"}</Button>
          </div>
        </ModalBody>
      </Modal>

      <PageHero
        eyebrow="Tabelas de frete"
        title="Taxa de Dificuldade de Entrega"
        description="Taxas de entrega (TDA, TDE, TRT, descarga, agendamento) cobradas por CPF/CNPJ do destinatário. O valor entra no frete simulado antes do ICMS/ISS."
        actions={
          <>
            <PageHeroRefreshButton onClick={loadRegras} loading={loading} />
            <Button
              color="info"
              className="btn-sm"
              onClick={openCreate}
              disabled={pageTab !== "regras"}
            >
              <FaPlus style={{ marginRight: 6 }} />
              Nova regra
            </Button>
          </>
        }
      >
        <div style={tabRowStyle}>
          {PAGE_TABS.map((aba) => (
            <button
              key={aba.key}
              type="button"
              style={tabPillStyle(pageTab === aba.key)}
              onClick={() => setPageTab(aba.key)}
            >
              {aba.label}
              {aba.key === "regras" && regras.length ? ` (${regras.length})` : ""}
            </button>
          ))}
        </div>
      </PageHero>

      {pageTab === "regras" ? (
        <>
          <FilterBand
            mode="client"
            searchLabel="Buscar regra"
            searchHint="CPF/CNPJ, nome da regra ou tipo"
            searchPlaceholder="Buscar por CPF/CNPJ, nome da regra ou tipo"
            searchValue={search}
            onSearchChange={setSearch}
            activeFiltersCount={activeFiltersCount}
            panelTitle="Filtros"
            panelDescription="Refine por transportadora e status da regra."
            onClear={hasFilters ? clearFilters : undefined}
            showActions={hasFilters}
          >
            <Row>
              <Col md="6">
                <Label for="tda-filtro-transportadora" style={filterLabelStyle}>
                  Transportadora
                </Label>
                <Select
                  inputId="tda-filtro-transportadora"
                  classNamePrefix="react-select"
                  styles={reactSelectStyles}
                  {...reactSelectPortalProps()}
                  options={transportadoraOptions}
                  value={transportadoraOption}
                  onChange={(opt) => setTransportadoraFilter(opt ? opt.value : TRANSP_ALL)}
                  isDisabled={hasTransportadoraSelection}
                  placeholder="Todas"
                />
                {hasTransportadoraSelection ? (
                  <div style={helperStyle}>Respeitando a transportadora selecionada no topo.</div>
                ) : null}
              </Col>
              <Col md="4">
                <Label for="tda-status-filtro" style={filterLabelStyle}>
                  Status
                </Label>
                <Select
                  inputId="tda-status-filtro"
                  classNamePrefix="react-select"
                  styles={reactSelectStyles}
                  {...reactSelectPortalProps()}
                  options={STATUS_OPTIONS}
                  value={statusOption}
                  onChange={(opt) => setStatusFilter(opt ? opt.value : STATUS_ALL)}
                  isSearchable={false}
                />
              </Col>
            </Row>
          </FilterBand>

          <MetricCardGroup
            title="Situação das regras"
            description="Contagem dentro do recorte aplicado acima."
          >
            <MetricCard
              label="No recorte"
              value={resumo.total}
              tone="neutral"
              iconClassName="tim-icons icon-bullet-list-67"
            />
            <MetricCard
              label="Ativas"
              value={resumo.ativas}
              tone="success"
              iconClassName="tim-icons icon-check-2"
            />
            <MetricCard
              label="Inativas"
              value={resumo.inativas}
              tone="pink"
              iconClassName="tim-icons icon-simple-remove"
            />
            <MetricCard
              label="Transportadoras"
              value={resumo.transportadoras}
              tone="info"
              iconClassName="tim-icons icon-delivery-fast"
            />
          </MetricCardGroup>

          <ShellCard
            className="frete-tda-lista"
            title="Regras cadastradas"
            description={loading ? "Carregando..." : `${filtered.length} resultado(s)`}
          >
            {loading ? (
              <div style={stateBoxStyle}>
                <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
                <span>Carregando regras...</span>
              </div>
            ) : !filtered.length ? (
              <div style={stateBoxStyle}>
                <span>Nenhuma regra encontrada para este recorte.</span>
                {hasFilters ? (
                  <Button size="sm" color="info" outline onClick={clearFilters}>
                    Limpar busca e filtros
                  </Button>
                ) : null}
              </div>
            ) : (
              <TableSurface>
                <Table size="sm" responsive hover className="frete-tda-table hub-thead-accent">
                  <thead className="text-primary">
                    <tr>
                      <th>Tipo</th>
                      <th>Transportadora</th>
                      <th>Alcance</th>
                      <th>Nome</th>
                      <th>Cálculo</th>
                      <th style={{ textAlign: "right" }}>Valor</th>
                      <th style={{ textAlign: "right" }}>Mínimo</th>
                      <th>Status</th>
                      <th style={{ width: 90 }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((item) => {
                      // PO1-3085: o tipo vem do PREFIXO do codigo e o alcance dos 4 campos de
                      // geografia mais o documento. O backend ja manda `alcance` pronto; o
                      // fallback local cobre resposta antiga em cache.
                      const tipoRegra = item.tipoRegra || tipoDoCodigo(item.codigo);
                      const alcance = item.alcance || rotuloAlcance(item);
                      return (
                        <tr key={item.id}>
                          <td style={{ verticalAlign: "middle" }}>
                            <Badge color={tipoBadgeColor(item.codigo)}>{tipoRegra}</Badge>
                          </td>
                          <td style={{ verticalAlign: "middle" }}>
                            {item.nomeTransportadora ||
                              transportadoraLabel(item.idTransportadora) ||
                              `#${item.idTransportadora}`}
                          </td>
                          <td style={{ verticalAlign: "middle" }}>
                            <div style={strongCellStyle}>{alcance}</div>
                            {item.cnpj ? (
                              <div style={subCellStyle}>{formatDocument(item.cnpj)}</div>
                            ) : null}
                          </td>
                          <td style={{ verticalAlign: "middle" }}>
                            <div style={strongCellStyle}>{item.nome}</div>
                            {item.descricao ? (
                              <div style={subCellStyle}>{item.descricao}</div>
                            ) : null}
                          </td>
                          <td style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
                            {item.tipo} / {item.por}
                          </td>
                          <td style={{ ...numericCellStyle, textAlign: "right" }}>
                            {formatValor(item)}
                          </td>
                          <td style={{ ...numericCellStyle, textAlign: "right" }}>
                            {formatMinimo(item)}
                          </td>
                          <td style={{ verticalAlign: "middle" }}>
                            <Button
                              color={isActive(item.status) ? "success" : "secondary"}
                              outline
                              size="sm"
                              onClick={() => toggleStatus(item)}
                              title={isActive(item.status) ? "Inativar regra" : "Ativar regra"}
                            >
                              {isActive(item.status) ? "Ativa" : "Inativa"}
                            </Button>
                          </td>
                          <td style={{ verticalAlign: "middle" }}>
                            <Button
                              color="info"
                              outline
                              size="sm"
                              title="Editar regra"
                              style={iconButtonStyle}
                              onClick={() => openEdit(item)}
                            >
                              <FaPen />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableSurface>
            )}
          </ShellCard>
        </>
      ) : (
        <ShellCard
          className="frete-tda-importar"
          title="Importar planilha"
          description="Cadastro em lote a partir de um arquivo .xlsx, com pré-visualização antes de gravar."
        >
          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="tda-import-transportadora">Transportadora</Label>
                <Input
                  id="tda-import-transportadora"
                  type="select"
                  value={importTransportadora}
                  disabled={hasTransportadoraSelection || isBusy}
                  onChange={(e) => {
                    setImportTransportadora(e.target.value);
                    setPreviewResult(null);
                    setImportResult(null);
                  }}
                >
                  <option value="">Selecione</option>
                  {transportadoras.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </Input>
                <div style={helperStyle}>
                  Todas as linhas da planilha entram para esta transportadora.
                </div>
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Planilha (.xlsx)</Label>
                {/* PO1-3887: o tema esconde o input file dentro de .form-group. */}
                <EscolherArquivos
                  key={fileInputKey}
                  accept=".xlsx"
                  disabled={isBusy}
                  ariaLabel="Planilha (.xlsx)"
                  rotulo="Escolher planilha"
                  onSelecionar={(files) => handleFileChange({ target: { files } })}
                />
                {selectedFile ? (
                  <div style={helperStyle}>Arquivo selecionado: {selectedFile.name}</div>
                ) : null}
              </FormGroup>
            </Col>
          </Row>

          <div style={insetPanelStyle}>
            <div style={insetEyebrowStyle}>Colunas esperadas (primeira linha = cabeçalho)</div>
            <Table size="sm" responsive style={{ marginBottom: 0 }} className="hub-thead-accent">
              <tbody>
                {FRETE_TDA_COLUNAS_PLANILHA.map((c) => (
                  <tr key={c.coluna}>
                    <td style={colunaNomeStyle}>{c.coluna}</td>
                    <td style={subCellStyle}>{c.descricao}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div style={helperStyle}>
              A coluna CNPJ aceita CPF (11 dígitos) ou CNPJ (14 dígitos) do destinatário. Se o Excel
              tirar os zeros à esquerda, o sistema os recupera na importação.
            </div>
          </div>

          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <Button color="info" onClick={handlePreview} disabled={!canPreview || isBusy}>
              {loadingPreview ? "Lendo planilha..." : "Pré-visualizar"}
            </Button>
            <Button color="success" onClick={handleImport} disabled={!canImport || isBusy}>
              <FaFileUpload style={{ marginRight: 6 }} />
              {loadingImport ? "Importando..." : "Importar"}
            </Button>
            <Button color="default" outline onClick={handleResetImport} disabled={isBusy}>
              Nova importação
            </Button>
          </div>

          {isBusy ? (
            <div style={stateBoxStyle}>
              <Spinner size="sm" style={{ color: AUTOPILOT_COLORS.accent }} />
              <span>
                {loadingPreview ? "Processando pré-visualização..." : "Importando regras..."}
              </span>
            </div>
          ) : null}

          {previewResult && !importResult
            ? renderResultado("Resultado da pré-visualização", previewResult, true)
            : null}
          {importResult ? renderResultado("Resultado da importação", importResult, false) : null}
        </ShellCard>
      )}
    </div>
  );
};

export default FreteTda;
